// ============================================================================
// Entity: Queues
// Key: queues
// Kind: entity
// Generated file. Do not edit manually.
// ============================================================================

import {
	getContactCodeTreesListQueryKey,
	getQueuesClientGetQueryKey,
	getQueuesClientGetQueryOptions,
	getQueuesDetailQueryKey,
	getQueuesDetailQueryOptions,
	getQueuesListQueryKey,
	getQueuesListQueryOptions,
	getQueuesRelationsContactCodeTreesAssignedQueryKey,
	getQueuesRelationsContactCodeTreesAssignedQueryOptions,
	getQueuesRelationsContactCodeTreesAvailableQueryKey,
	getQueuesRelationsContactCodeTreesAvailableQueryOptions,
	getQueuesRelationsTeamsAssignedQueryKey,
	getQueuesRelationsTeamsAssignedQueryOptions,
	getQueuesRelationsTeamsAvailableQueryKey,
	getQueuesRelationsTeamsAvailableQueryOptions,
	getQueuesRelationsUsersAssignedQueryKey,
	getQueuesRelationsUsersAssignedQueryOptions,
	getQueuesRelationsUsersAvailableQueryKey,
	getQueuesRelationsUsersAvailableQueryOptions,
	getTeamsListQueryKey,
	getUsersListQueryKey,
	queuesClientDeleteQueueIdRegistration,
	queuesClientPutQueueIdRegistration,
	queuesCreate,
	queuesDelete,
	queuesRelationsContactCodeTreesAssign,
	queuesRelationsContactCodeTreesUnassign,
	queuesRelationsContactCodeTreesUpdate,
	queuesRelationsTeamsAssign,
	queuesRelationsTeamsUnassign,
	queuesRelationsUsersAssign,
	queuesRelationsUsersUnassign,
	queuesUpdate,
} from '../api/backstage'
// #region Imports
import type {
	ContactCodeTreeQueue,
	CreateOrUpdateQueueRequest,
	CreateTeamQueueResponse,
	PagedResultOfContactCodeTreeQueue,
	PagedResultOfContactCodeTreeSummary,
	PagedResultOfQueueSummary,
	PagedResultOfTeamQueueResponse,
	PagedResultOfTeamSummary,
	PagedResultOfUserDto,
	PagedResultOfUserQueueDto,
	QueueDetails,
	QueueSummary,
	UpdateContactCodeTreeQueueRequest,
	UserQueueDto,
} from '../api/backstage.schemas'
import type { EntityConfig, ListQueryParams } from './entity-config.types'
import {
	defineMutationEndpoint,
	defineQueryEndpoint,
} from './entity-config.types'

// #endregion

// #region Entity Endpoints
const queuesEntityEndpoints = {
	create: defineMutationEndpoint<
		QueueSummary,
		CreateOrUpdateQueueRequest,
		undefined
	>({
		key: 'queues.create',
		method: 'POST',
		path: '/admin/queues',
		mutationOptions: () => ({
			mutationFn: (variables: { data: CreateOrUpdateQueueRequest }) =>
				queuesCreate(variables.data),
		}),
		invalidates: [(_context) => getQueuesListQueryKey()],
	}),
	delete: defineMutationEndpoint<void, undefined, { queueId: number }>({
		key: 'queues.delete',
		method: 'DELETE',
		path: '/admin/queues/{queueId}',
		pathParams: ['queueId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				queueId: number | string | null | undefined
			}) => queuesDelete(variables.queueId),
		}),
		invalidates: [(_context) => getQueuesListQueryKey()],
	}),
	detail: defineQueryEndpoint<QueueDetails, { queueId: number }, undefined>({
		key: 'queues.detail',
		method: 'GET',
		path: '/admin/queues/{queueId}',
		pathParams: ['queueId'] as const,
		queryKey: (args) => getQueuesDetailQueryKey(args?.pathParams?.queueId),
		queryOptions: (args) =>
			getQueuesDetailQueryOptions(args?.pathParams?.queueId),
	}),
	list: defineQueryEndpoint<
		PagedResultOfQueueSummary,
		undefined,
		ListQueryParams
	>({
		key: 'queues.list',
		method: 'GET',
		path: '/admin/queues',
		queryKey: (args) => getQueuesListQueryKey(args?.queryParams),
		queryOptions: (args) => getQueuesListQueryOptions(args?.queryParams),
	}),
	update: defineMutationEndpoint<
		QueueSummary,
		CreateOrUpdateQueueRequest,
		{ queueId: number }
	>({
		key: 'queues.update',
		method: 'PUT',
		path: '/admin/queues/{queueId}',
		pathParams: ['queueId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				queueId: number | string | null | undefined
				data: CreateOrUpdateQueueRequest
			}) => queuesUpdate(variables.queueId, variables.data),
		}),
		invalidates: [
			(_context) => getQueuesListQueryKey(),
			(context) => getQueuesDetailQueryKey(context.entityId ?? context.ownerId),
		],
	}),
} as const
// #endregion

// #region Relations
// #region Relation: ContactCodeTrees
// Target Entity: contactCodeTrees
const queuesContactCodeTreesRelation = {
	key: 'contactCodeTrees',
	targetEntity: 'contactCodeTrees',
	endpoints: {
		assign: defineMutationEndpoint<
			ContactCodeTreeQueue[],
			(number | string)[],
			{ queueId: number }
		>({
			key: 'queues.relations.contactCodeTrees.assign',
			method: 'POST',
			path: '/admin/queues/{queueId}/contactcodetrees',
			pathParams: ['queueId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					queueId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					queuesRelationsContactCodeTreesAssign(
						variables.queueId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getQueuesRelationsContactCodeTreesAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getQueuesRelationsContactCodeTreesAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getQueuesListQueryKey(),
				(_context) => getContactCodeTreesListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfContactCodeTreeQueue,
			{ queueId: number },
			ListQueryParams
		>({
			key: 'queues.relations.contactCodeTrees.assigned',
			method: 'GET',
			path: '/admin/queues/{queueId}/contactcodetrees',
			pathParams: ['queueId'] as const,
			queryKey: (args) =>
				getQueuesRelationsContactCodeTreesAssignedQueryKey(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getQueuesRelationsContactCodeTreesAssignedQueryOptions(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfContactCodeTreeSummary,
			{ queueId: number },
			ListQueryParams
		>({
			key: 'queues.relations.contactCodeTrees.available',
			method: 'GET',
			path: '/admin/queues/{queueId}/assignable-contactcodetrees',
			pathParams: ['queueId'] as const,
			queryKey: (args) =>
				getQueuesRelationsContactCodeTreesAvailableQueryKey(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getQueuesRelationsContactCodeTreesAvailableQueryOptions(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ queueId: number }
		>({
			key: 'queues.relations.contactCodeTrees.unassign',
			method: 'DELETE',
			path: '/admin/queues/{queueId}/contactcodetrees',
			pathParams: ['queueId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					queueId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					queuesRelationsContactCodeTreesUnassign(
						variables.queueId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getQueuesRelationsContactCodeTreesAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getQueuesRelationsContactCodeTreesAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getQueuesListQueryKey(),
				(_context) => getContactCodeTreesListQueryKey(),
			],
		}),
		update: defineMutationEndpoint<
			ContactCodeTreeQueue,
			UpdateContactCodeTreeQueueRequest,
			{ queueId: number; mappingId: number }
		>({
			key: 'queues.relations.contactCodeTrees.update',
			method: 'PUT',
			path: '/admin/queues/{queueId}/contactcodetrees/{mappingId}',
			pathParams: ['queueId', 'mappingId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					queueId: number | string | null | undefined
					mappingId: number | string | null | undefined
					data: UpdateContactCodeTreeQueueRequest
				}) =>
					queuesRelationsContactCodeTreesUpdate(
						variables.queueId,
						variables.mappingId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getQueuesRelationsContactCodeTreesAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getQueuesRelationsContactCodeTreesAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getQueuesListQueryKey(),
				(_context) => getContactCodeTreesListQueryKey(),
			],
		}),
	},
} as const
// #endregion

// #region Relation: Teams
// Target Entity: teams
const queuesTeamsRelation = {
	key: 'teams',
	targetEntity: 'teams',
	endpoints: {
		assign: defineMutationEndpoint<
			CreateTeamQueueResponse[],
			(number | string)[],
			{ queueId: number }
		>({
			key: 'queues.relations.teams.assign',
			method: 'POST',
			path: '/admin/queues/{queueId}/teams',
			pathParams: ['queueId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					queueId: number | string | null | undefined
					data: (number | string)[]
				}) => queuesRelationsTeamsAssign(variables.queueId, variables.data),
			}),
			invalidates: [
				(context) =>
					getQueuesRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getQueuesRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getQueuesListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfTeamQueueResponse,
			{ queueId: number },
			ListQueryParams
		>({
			key: 'queues.relations.teams.assigned',
			method: 'GET',
			path: '/admin/queues/{queueId}/teams',
			pathParams: ['queueId'] as const,
			queryKey: (args) =>
				getQueuesRelationsTeamsAssignedQueryKey(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getQueuesRelationsTeamsAssignedQueryOptions(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfTeamSummary,
			{ queueId: number },
			ListQueryParams
		>({
			key: 'queues.relations.teams.available',
			method: 'GET',
			path: '/admin/queues/{queueId}/assignable-teams',
			pathParams: ['queueId'] as const,
			queryKey: (args) =>
				getQueuesRelationsTeamsAvailableQueryKey(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getQueuesRelationsTeamsAvailableQueryOptions(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ queueId: number }
		>({
			key: 'queues.relations.teams.unassign',
			method: 'DELETE',
			path: '/admin/queues/{queueId}/teams',
			pathParams: ['queueId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					queueId: number | string | null | undefined
					data: (number | string)[]
				}) => queuesRelationsTeamsUnassign(variables.queueId, variables.data),
			}),
			invalidates: [
				(context) =>
					getQueuesRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getQueuesRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getQueuesListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
	},
} as const
// #endregion

// #region Relation: Users
// Target Entity: users
const queuesUsersRelation = {
	key: 'users',
	targetEntity: 'users',
	endpoints: {
		assign: defineMutationEndpoint<
			UserQueueDto[],
			(number | string)[],
			{ queueId: number }
		>({
			key: 'queues.relations.users.assign',
			method: 'POST',
			path: '/admin/queues/{queueId}/users',
			pathParams: ['queueId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					queueId: number | string | null | undefined
					data: (number | string)[]
				}) => queuesRelationsUsersAssign(variables.queueId, variables.data),
			}),
			invalidates: [
				(context) =>
					getQueuesRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getQueuesRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getQueuesListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfUserQueueDto,
			{ queueId: number },
			ListQueryParams
		>({
			key: 'queues.relations.users.assigned',
			method: 'GET',
			path: '/admin/queues/{queueId}/users',
			pathParams: ['queueId'] as const,
			queryKey: (args) =>
				getQueuesRelationsUsersAssignedQueryKey(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getQueuesRelationsUsersAssignedQueryOptions(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfUserDto,
			{ queueId: number },
			ListQueryParams
		>({
			key: 'queues.relations.users.available',
			method: 'GET',
			path: '/admin/queues/{queueId}/assignable-users',
			pathParams: ['queueId'] as const,
			queryKey: (args) =>
				getQueuesRelationsUsersAvailableQueryKey(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getQueuesRelationsUsersAvailableQueryOptions(
					args?.pathParams?.queueId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ queueId: number }
		>({
			key: 'queues.relations.users.unassign',
			method: 'DELETE',
			path: '/admin/queues/{queueId}/users',
			pathParams: ['queueId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					queueId: number | string | null | undefined
					data: (number | string)[]
				}) => queuesRelationsUsersUnassign(variables.queueId, variables.data),
			}),
			invalidates: [
				(context) =>
					getQueuesRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getQueuesRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getQueuesListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
	},
} as const
// #endregion
// #endregion

// #region Client Endpoints
const queuesClientEndpoints = {
	deleteQueueIdRegistration: defineMutationEndpoint<
		UserQueueDto,
		undefined,
		{ queueId: number }
	>({
		key: 'queues.client.deleteQueueIdRegistration',
		method: 'DELETE',
		path: '/api/queues/{queueId}/registration',
		pathParams: ['queueId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				queueId: number | string | null | undefined
			}) => queuesClientDeleteQueueIdRegistration(variables.queueId),
		}),
	}),
	get: defineQueryEndpoint<UserQueueDto[], undefined, undefined>({
		key: 'queues.client.get',
		method: 'GET',
		path: '/api/queues',
		queryKey: (_args) => getQueuesClientGetQueryKey(),
		queryOptions: (_args) => getQueuesClientGetQueryOptions(),
	}),
	putQueueIdRegistration: defineMutationEndpoint<
		UserQueueDto,
		undefined,
		{ queueId: number }
	>({
		key: 'queues.client.putQueueIdRegistration',
		method: 'PUT',
		path: '/api/queues/{queueId}/registration',
		pathParams: ['queueId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				queueId: number | string | null | undefined
			}) => queuesClientPutQueueIdRegistration(variables.queueId),
		}),
	}),
} as const
// #endregion

// #region Entity Config Export
export const queuesEntityConfig = {
	key: 'queues',
	kind: 'entity',
	entity: {
		idField: 'id',
		idParam: 'queueId',
		routePath: '/queues',
	},
	endpoints: queuesEntityEndpoints,
	relations: {
		contactCodeTrees: queuesContactCodeTreesRelation,
		teams: queuesTeamsRelation,
		users: queuesUsersRelation,
	},
	client: queuesClientEndpoints,
} as const satisfies EntityConfig<'queues'>
// #endregion
